/**
 * GetComponentDetailCard — renders the `get_component_detail` tool result.
 *
 * The backend returns the get_analysis payload sliced to a single component
 * (component_details as a single-entry dict + the requested `component` key).
 * This card runs the shared buildCompScores() over that payload, picks the
 * requested component, and hands the exact { compKey, tsId, fields, details,
 * score } shape to the EXISTING ComponentDetailCard — the same shape the click
 * path (ChatScoreBreakdownCard row -> injected `component_detail`) already uses.
 * No scoring math is duplicated here or in Python.
 *
 * Renders nothing (prose carries) when analysis or the requested component is
 * unavailable, preserving "a card renders OR prose carries, never both".
 *
 * HANDOFF_CHAT_GET_COMPONENT_DETAIL (T-37).
 */
import { buildCompScores } from '../../../components/analysis/CompCard/compConfig';
import type { Analysis } from '../../../api/types';
import type { ToolResultProps } from '../lib/component-registry';
import { ComponentDetailCard } from './ComponentDetailCard';

interface GetComponentDetailResult {
  component?: string;
  term_sheet_id?: number | string | null;
  analysis_available?: boolean;
  component_details_available?: boolean;
  fields?: Record<string, unknown> | null;
}

export function GetComponentDetailCard({ result }: ToolResultProps) {
  const r = (result ?? {}) as GetComponentDetailResult & Partial<Analysis>;

  // Unavailable -> let the model's prose carry the message.
  if (
    r.analysis_available === false ||
    r.component_details_available === false ||
    !r.component_details
  ) {
    return null;
  }

  const comps = buildCompScores(r as Analysis);
  const comp = comps.find((c) => c.key === r.component);
  if (!comp) {
    return null;
  }

  return (
    <ComponentDetailCard
      result={{
        compKey: comp.key,
        tsId: r.term_sheet_id ?? null,
        fields: r.fields ?? null,
        details: comp.details,
        score: comp.score,
      }}
    />
  );
}
